// Resolve a deep-linked deliverable id to the top-level schedule row that holds
// it, for the cross-link page-jump (see ../shared/useDeepLinkPageJump). The
// deliverable table paginates by the top-level row (parent / one-off / orphan);
// children render nested under their parent. A linked occurrence that is a child
// of a recurring parent therefore resolves to its PARENT's index, with
// `expandParentId` set so the caller can auto-expand that parent and reveal the
// child. Returns index -1 when the id isn't in the rows (not loaded / archived).

import type { Deliverable } from "../../../../types";
import type { Row } from "./groupDeliverables";

export interface DeliverableDeepLinkTarget {
  /** 0-based index of the top-level row in the paginated list, or -1. */
  index: number;
  /** Parent schedule id to expand when the match was a nested child. */
  expandParentId: string | null;
  /** The matched deliverable itself (the CHILD for a nested match), or null.
   *  Lets the caller open the row's view expand without a second lookup —
   *  non-null exactly when index >= 0. */
  item: Deliverable | null;
}

export function findDeliverableDeepLinkTarget(
  itemId: string | null | undefined,
  rows: readonly Row[],
): DeliverableDeepLinkTarget {
  if (!itemId) return { index: -1, expandParentId: null, item: null };
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.kind === "parent") {
      if (row.deliverable.id === itemId)
        return { index: i, expandParentId: null, item: row.deliverable };
      const child = row.children.find((c) => c.deliverable.id === itemId);
      if (child)
        return {
          index: i,
          expandParentId: row.deliverable.id,
          item: child.deliverable,
        };
    } else if (row.deliverable.id === itemId) {
      return { index: i, expandParentId: null, item: row.deliverable };
    }
  }
  return { index: -1, expandParentId: null, item: null };
}
